import { formatRelativeTime } from "@/lib/format";
import { Badge, severityToBadgeVariant, severityLabel } from "@/components/ui/Badge";
import type { AttentionItem, AttentionSeverity } from "@/types";

interface AttentionListProps {
  items: AttentionItem[];
  loading?: boolean;
  onOpenIncident?: (incidentId: string) => void;
}

const severityIcon: Record<AttentionSeverity, string> = {
  critical: "🔴",
  high: "🟠",
  medium: "🟡",
  low: "⚪",
};

export function AttentionList({ items, loading, onOpenIncident }: AttentionListProps) {
  if (loading) {
    return (
      <div className="glass-panel attention-empty" aria-busy="true">
        Loading incidents…
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="glass-panel attention-empty">
        No open incidents reported.
      </div>
    );
  }

  return (
    <ul className="attention-list" aria-label="Items needing attention">
      {items.map((item) => (
        <li key={item.id} className="attention-item">
          <span className="attention-item__icon" aria-hidden="true">
            {severityIcon[item.severity]}
          </span>
          <div className="attention-item__body">
            <div className="attention-item__row">
              <span className="attention-item__tenant">{item.tenantName}</span>
              <Badge variant={severityToBadgeVariant(item.severity)}>
                {severityLabel(item.severity)}
              </Badge>
              <code className="attention-item__check">{item.checkId}</code>
            </div>
            <div className="attention-item__problem">{item.problem}</div>
            <div className="attention-item__action-label">{item.recommendedAction}</div>
            {onOpenIncident ? (
              <button
                type="button"
                className="btn btn-primary attention-item__review"
                onClick={() => onOpenIncident(item.id)}
              >
                View Incident
              </button>
            ) : null}
            <time className="attention-item__time" dateTime={item.occurredAt}>
              {formatRelativeTime(item.occurredAt)}
            </time>
          </div>
        </li>
      ))}
    </ul>
  );
}
